import React from 'react';
import { Modal, View, StyleSheet, TouchableWithoutFeedback, ScrollView } from 'react-native';
import { Text, List, useTheme } from 'react-native-paper';
import convert, { Measure } from 'convert-units';
import { Unit } from '../../types/table';

interface UnitModalProps {
    visible: boolean;
    onDismiss: () => void;
    currentUnit?: Unit;
    onUnitSelect: (unit: Unit) => void;
    measure?: Measure;
}

export const UnitModal: React.FC<UnitModalProps> = ({
    visible,
    onDismiss,
    currentUnit,
    onUnitSelect,
    measure,
}) => {
    const { colors } = useTheme();
    const availableUnits = React.useMemo(() => {
        if (!measure) {
            // No measure provided
            return [];
        }
        try {
            const units = convert().possibilities(measure);
            // Available units for measure
            return units;
        } catch (error) {
            // Error getting unit possibilities
            return [];
        }
    }, [measure, currentUnit]);

    if (!visible) return null;

    const handleUnitSelect = (unit: Unit) => {
        if (!measure) {
            // Cannot select unit without measure type
            return;
        }

        // Unit selection

        // Verify the unit is valid for this measure
        if (!availableUnits.includes(unit)) {
            // Selected unit is not valid for measure
            return;
        }

        onUnitSelect(unit);
        onDismiss();
    };

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onDismiss}
        >
            <TouchableWithoutFeedback onPress={onDismiss}>
                <View style={[styles.modalContainer, { backgroundColor: colors.backdrop }]}>
                    <TouchableWithoutFeedback>
                        <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
                            <Text variant="titleMedium" style={styles.title}>
                                Select Unit ({measure})
                            </Text>
                            <ScrollView style={styles.scrollView}>
                                <List.Section>
                                    {availableUnits.map((unit) => (
                                        <List.Item
                                            key={unit}
                                            title={unit}
                                            onPress={() => handleUnitSelect(unit as Unit)}
                                            left={props => (
                                                <List.Icon
                                                    {...props}
                                                    icon={unit === currentUnit ? 'check' : 'blank'}
                                                />
                                            )}
                                        />
                                    ))}
                                </List.Section>
                            </ScrollView>
                        </View>
                    </TouchableWithoutFeedback>
                </View>
            </TouchableWithoutFeedback>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContent: {
        width: '100%',
        maxWidth: 400,
        borderRadius: 8,
        padding: 16,
        maxHeight: '80%',
    },
    scrollView: {
        flexGrow: 0,
    },
    title: {
        marginBottom: 16,
        textAlign: 'center',
    },
});
